"use client"

import { useEffect } from "react"
import { ChevronLeft, ChevronRight, Download, X } from "lucide-react"

export interface LightboxScreenshot {
  id: string
  url: string | null
  fileName: string
}

/**
 * Fullscreen screenshot lightbox, extracted verbatim from the retired
 * product-feedback-content.tsx. An overlay (not a glass surface), so it stays
 * opaque per the design system's modal rule.
 */
export function ScreenshotLightbox({
  screenshots,
  currentIndex,
  onClose,
  onNavigate,
}: {
  screenshots: ReadonlyArray<LightboxScreenshot>
  currentIndex: number
  onClose: () => void
  onNavigate: (index: number) => void
}) {
  const current = screenshots[currentIndex]

  // Esc closes; arrows page through. Makes "how do I get out of here" obvious
  // alongside the visible close button and click-outside-to-close.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose()
      else if (e.key === "ArrowLeft" && currentIndex > 0) onNavigate(currentIndex - 1)
      else if (e.key === "ArrowRight" && currentIndex < screenshots.length - 1)
        onNavigate(currentIndex + 1)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [currentIndex, screenshots.length, onClose, onNavigate])

  if (!current) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
      onClick={onClose}
    >
      {/* Close affordance pinned to the viewport corner (not the image corner) so
          it's obvious regardless of the screenshot's size. */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white text-gray-700 shadow-lg transition-colors hover:bg-gray-100"
      >
        <X className="h-5 w-5" />
      </button>

      <div className="relative max-h-[90vh] max-w-[90vw]" onClick={(e) => e.stopPropagation()}>
        {current.url ? (
          <img
            src={current.url}
            alt={current.fileName}
            className="max-h-[80vh] max-w-[85vw] rounded-lg object-contain shadow-2xl"
          />
        ) : (
          <div className="flex h-64 w-96 items-center justify-center rounded-lg bg-gray-100">
            <p className="text-sm text-gray-500">Unable to load image</p>
          </div>
        )}

        <div className="mt-2 flex items-center justify-between rounded-lg bg-white/90 px-3 py-2 backdrop-blur-xs">
          <span className="text-xs text-gray-500">
            {current.fileName} ({currentIndex + 1} of {screenshots.length})
          </span>
          <div className="flex items-center gap-2">
            {screenshots.length > 1 && (
              <>
                <button
                  type="button"
                  className="flex h-7 w-7 items-center justify-center rounded border border-[#DFE1E6] hover:bg-gray-100 disabled:opacity-50"
                  disabled={currentIndex === 0}
                  onClick={() => onNavigate(currentIndex - 1)}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  className="flex h-7 w-7 items-center justify-center rounded border border-[#DFE1E6] hover:bg-gray-100 disabled:opacity-50"
                  disabled={currentIndex === screenshots.length - 1}
                  onClick={() => onNavigate(currentIndex + 1)}
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </>
            )}
            {current.url && (
              <a
                href={current.url}
                download={current.fileName}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-7 items-center gap-1 rounded border border-[#DFE1E6] px-2 text-xs hover:bg-gray-100"
              >
                <Download className="h-3 w-3" />
                Download
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
